import { Tag, Tooltip } from 'antd'
import type { ColumnType } from 'antd/es/table'
import type { TFunction } from 'i18next'

import type { SettlementSummaryRoot } from '@repo/sdk'

import { StatusBadge } from '../../components/StatusBadge'
import { formatKrw } from '@repo/shared'
import type { SettlementStatusConfigMap } from './settlementStatusConfig'
import { formatOrderBriefLabel } from '../orders/orderDisplay'
import { formatKstDateTime } from '../../utils/formatKstDateTime'

/**
 * Gross amount this settlement is computed on — for a SHIPPING row the shipping fee itself. A
 * server from before the field existed sends none; the order total is the pre-split basis then.
 */
function settlementBasisAmount(record: SettlementSummaryRoot): number {
  return record.totalPaymentAmount?.amount ?? record.order.totalAmount
}

export interface SettlementHistoryColumnProps {
  t: TFunction
  statusConfig: SettlementStatusConfigMap
}

export function createSettlementHistoryColumns({
  t,
  statusConfig,
}: SettlementHistoryColumnProps): ColumnType<SettlementSummaryRoot>[] {
  return [
    {
      title: t('common.columns.settlementId'),
      dataIndex: 'settlementId',
      key: 'settlementId',
      width: 120,
      render: (value: string) => (
        <Tooltip title={value}>
          <span>{value.slice(0, 8)}...</span>
        </Tooltip>
      ),
    },
    {
      // #44 2단계: a shipping charge settles on its own row (SHIPPING); the order shown is the
      // one that paid the charge.
      title: t('common.columns.settlementKind'),
      key: 'settlementKind',
      width: 90,
      render: (_: unknown, record: SettlementSummaryRoot) =>
        record.settlementKind === 'SHIPPING' ? (
          <Tag color="geekblue" data-testid={`settlement-kind-shipping-${record.settlementId}`}>
            {t('pages.settlements.kind.shipping')}
          </Tag>
        ) : (
          <Tag>{t('pages.settlements.kind.product')}</Tag>
        ),
    },
    {
      title: t('common.columns.order'),
      key: 'orderId',
      width: 300,
      render: (_: unknown, record: SettlementSummaryRoot) => (
        <span>{formatOrderBriefLabel(record.order)}</span>
      ),
    },
    {
      title: t('common.columns.totalPaymentAmount'),
      key: 'totalPaymentAmount',
      width: 140,
      align: 'right' as const,
      // The settlement's own basis: for a SHIPPING row that is the shipping fee, not the order's
      // total (which is the paying order's products).
      render: (_: unknown, record: SettlementSummaryRoot) =>
        formatKrw(settlementBasisAmount(record)),
    },
    {
      title: t('common.columns.platformFee'),
      key: 'platformFee',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, record: SettlementSummaryRoot) =>
        formatKrw(settlementBasisAmount(record) - record.payoutAmount.amount),
    },
    {
      title: t('common.columns.grossPayoutAmount'),
      key: 'payoutAmount',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, record: SettlementSummaryRoot) => formatKrw(record.payoutAmount.amount),
    },
    {
      title: t('common.columns.netPayoutAmount'),
      key: 'netPayoutAmount',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, record: SettlementSummaryRoot) =>
        record.netPayoutAmount ? formatKrw(record.netPayoutAmount.amount) : '-',
    },
    {
      title: t('common.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_: unknown, record: SettlementSummaryRoot) => (
        <StatusBadge status={record.status} configMap={statusConfig} />
      ),
    },
    {
      title: t('common.columns.orderConfirmedAt'),
      dataIndex: 'orderConfirmedAt',
      key: 'orderConfirmedAt',
      width: 150,
      render: (value: string) => (value ? formatKstDateTime(value, 'YYYY-MM-DD HH:mm') : '-'),
    },
  ]
}
